import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import type { Reading } from "@/api/reseller/mqtt-types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { isValidLang, type Lang, ROUTE_SLUGS } from "@/config/routes";

type Props = {
	readings: Reading[];
};

export function RmMqttReadingsTable({ readings }: Props) {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { lang } = useParams<{ lang: string }>();
	const currentLang: Lang = lang && isValidLang(lang) ? lang : "pl";

	if (readings.length === 0) return null;

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center justify-between">
				<h3 className="text-base font-semibold">
					{t("dashboard.mqttReadings.title")}
				</h3>
				<Button
					variant="ghost"
					size="sm"
					onClick={() =>
						navigate(`/${currentLang}/${ROUTE_SLUGS.readings[currentLang]}`)
					}
				>
					{t("dashboard.mqttReadings.seeAll")}
				</Button>
			</div>
			<div className="overflow-hidden rounded-md border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>{t("dashboard.mqttReadings.colSource")}</TableHead>
							<TableHead>{t("mqttReadingsTable.colReadingDate")}</TableHead>
							<TableHead>{t("mqttSources.colStatus")}</TableHead>
							<TableHead />
						</TableRow>
					</TableHeader>
					<TableBody>
						{readings.map((row) => (
							<TableRow key={row.id}>
								<TableCell className="font-mono text-sm">
									{row.mqtt_source?.sn ?? row.advant_source?.sn ?? "—"}
								</TableCell>
								<TableCell>
									{new Date(row.reading_date).toLocaleString(i18n.language, {
										dateStyle: "short",
										timeStyle: "short",
									})}
								</TableCell>
								<TableCell>
									<Badge variant={row.completed ? "default" : "outline"}>
										{row.completed
											? t("mqttReadingsTable.statusCompleted")
											: t("mqttReadingsTable.statusInProgress")}
									</Badge>
								</TableCell>
								<TableCell>
									<Button
										variant="ghost"
										size="sm"
										onClick={() =>
											navigate(
												`/${currentLang}/${ROUTE_SLUGS.readings[currentLang]}/${row.id}`,
											)
										}
									>
										{t("dashboard.mqttReadings.details")}
									</Button>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</div>
	);
}
